import moment, { Moment } from 'moment';
import { ReasonForBlockedDate } from 'src/screens/ManualOrder/types';
import { isDayDisabledForOrder } from '../../dayRules';
import { DateDataForOrdering } from '../../types';

/**
 * Whether the date picker may offer this date: it must be one of the days the grid shows, and the grid's
 * own rule must allow it.
 * @param editedShiftDate - the date of the order being edited, if any
 */
const isDateSelectable = (
    date: Moment | Date | null,
    datesForOrders: DateDataForOrdering[],
    isShiftSelected: boolean,
    editedShiftDate?: Date | Moment | string | null
): boolean => {
    if (!date) return false;
    const day = moment(date);

    const match = datesForOrders.find((d) => moment(d.date).isSame(day, 'day'));
    if (!match) return false;

    const { reason } = match.selectableConfig || {};
    const isReplacingOrderOnDayWithMaxOrders =
        reason === ReasonForBlockedDate.PassengerHasTwoOrdersOnDate &&
        !!editedShiftDate &&
        moment(editedShiftDate).isSame(day, 'day') &&
        isShiftSelected;

    return !isDayDisabledForOrder(reason, isShiftSelected, isReplacingOrderOnDayWithMaxOrders);
};

/** The first and last day the picker offers: the range of the days grid */
const getDatesRange = (datesForOrders: DateDataForOrdering[]) => {
    if (!datesForOrders.length) return null;
    const days = datesForOrders.map((d) => moment(d.date));
    return { min: moment.min(days), max: moment.max(days) };
};

/** Sunday to Thursday, in moment's day() numbering (Sunday = 0) */
export const WORK_WEEK_DAYS = [0, 1, 2, 3, 4];

/**
 * The days a "from – to" range selects: every day of the grid between the two dates (either order), on
 * one of the chosen weekdays, that the grid allows. Days the grid blocks are skipped and counted.
 */
const buildRangeDates = (
    from: Moment | Date | null,
    to: Moment | Date | null,
    weekdays: number[],
    datesForOrders: DateDataForOrdering[],
    isShiftSelected: boolean,
    editedShiftDate?: Date | Moment | string | null
): { dates: Moment[]; skippedBlocked: number } => {
    if (!from || !to || !moment(from).isValid() || !moment(to).isValid()) return { dates: [], skippedBlocked: 0 };

    const start = moment.min(moment(from), moment(to)).startOf('day');
    const end = moment.max(moment(from), moment(to)).startOf('day');

    const dates: Moment[] = [];
    let skippedBlocked = 0;

    datesForOrders
        .map((d) => moment(d.date).startOf('day'))
        .filter((day) => day.isBetween(start, end, 'day', '[]') && weekdays.includes(day.day()))
        .sort((a, b) => a.valueOf() - b.valueOf())
        .forEach((day) => {
            if (isDateSelectable(day, datesForOrders, isShiftSelected, editedShiftDate)) dates.push(day);
            else skippedBlocked += 1;
        });

    return { dates, skippedBlocked };
};

const orderDatesDetailsHelpers = { isDateSelectable, getDatesRange, buildRangeDates };

export default orderDatesDetailsHelpers;
